import { Component, OnInit, OnDestroy, HostListener, inject, ChangeDetectionStrategy, signal, DestroyRef } from '@angular/core';
import { Router, RouterOutlet, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { NavigationComponent } from './navigation/navigation.component';
import { TopnavbarComponent } from './topnavbar/topnavbar.component';
import { FooterComponent } from './footer/footer.component';
import { LoadingComponent } from './loading/loading.component';
import { LoginService } from '../core/services/login.service';
import { ChatSidebarComponent } from './chat-sidebar/chat-sidebar.component';
import { ChatService } from '../core/services/chat.service';
import { GuideOverlayComponent } from '../shared/components/guide-overlay/guide-overlay.component';
import { UserNotificationsService } from '../core/services/user-notifications.service';
import { SidebarService } from '../core/services/sidebar.service';
import { StelaPageHeadComponent } from '../shared/layout/components/stela-page-head/stela-page-head.component';
import { StelaLayoutContextService } from '../shared/layout/services/stela-layout-context.service';
import { ThemeStateService } from '../core/theme/theme-state.service';

@Component({
    selector: 'app-layout',
    imports: [
    RouterOutlet,
    NavigationComponent,
    TopnavbarComponent,
    FooterComponent,
    LoadingComponent,
    ChatSidebarComponent,
    GuideOverlayComponent,
    StelaPageHeadComponent
],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
@if (isLoggedIn) {
  <div class="stela-shell">
    <!-- Sidebar (grid column 1) -->
    <aside class="stela-grid-sidebar">
      <app-navigation></app-navigation>
    </aside>

    <!-- Content (grid column 2) -->
    <section class="stela-grid-content"
             [class.stela-chat-shift]="chatService.visible() && !isMobile">
      <!-- Topbar -->
      <app-topnavbar></app-topnavbar>

      <!-- Main content: page-head + router-outlet share the same gutter so
           the heading aligns horizontally with the card/grid below. The
           loader is scoped to this block (not full-screen) so the sidebar
           and topbar stay visible/interactive while a route loads. -->
      <main class="stela-page-main">
        <app-loading [visible]="showLoading()"></app-loading>
        @if (layoutContext.pageTitle()) {
          <stela-page-head
            [title]="layoutContext.pageTitle()!"
            [breadcrumbs]="layoutContext.breadcrumbs()"
            [metadata]="layoutContext.pageMetadata()"
            [actionsTemplate]="layoutContext.actionsTemplate()"
            [kpisTemplate]="layoutContext.kpisTemplate()"
            [chipsTemplate]="layoutContext.chipsTemplate()" />
        }
        <router-outlet></router-outlet>
      </main>

      <!-- Footer -->
      <app-footer></app-footer>
    </section>

    <app-chat-sidebar></app-chat-sidebar>
    <app-guide-overlay></app-guide-overlay>
  </div>
}
`
})
export class LayoutComponent implements OnInit, OnDestroy {
    private loginService = inject(LoginService);
    public chatService = inject(ChatService);
    readonly layoutContext = inject(StelaLayoutContextService);
    private userNotificationsService = inject(UserNotificationsService);
    private sidebarService = inject(SidebarService);
    private themeKit = inject(ThemeStateService);
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);

    isLoggedIn = false;

    isMobile = window.innerWidth < 768;

    /**
     * Driven by router events: shows the loader (scoped to the main content
     * block, see template) while a navigation is in flight, so the user gets
     * feedback during the 1–4 s window that would otherwise be a blank area
     * between routes. Sidebar and topbar are outside its scope on purpose.
     */
    showLoading = signal(false);
    public inactivityCheckInterval: any;
    private darkMediaQuery: MediaQueryList | null = null;
    private darkModeListener: ((event: MediaQueryListEvent) => void) | null = null;
    private resizeTimer: any = null;

    ngOnInit(): void {
        this.isLoggedIn = this.loginService.isLoggedIn();

        // Show the global loader while routing between modules.
        this.router.events
            .pipe(
                filter(e => e instanceof NavigationStart
                         || e instanceof NavigationEnd
                         || e instanceof NavigationCancel
                         || e instanceof NavigationError),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(e => this.showLoading.set(e instanceof NavigationStart));

        // Set theme from persisted preference; fallback to OS preference when not persisted.
        // ThemeStateService is the single owner of the DOM/localStorage writes; a preference
        // read from the OS (no saved 'theme' yet) is applied but not persisted, so it keeps
        // following the OS on future loads until the user picks explicitly.
        const persistedTheme = localStorage.getItem('theme');
        const theme = this.resolveInitialTheme(persistedTheme);
        this.themeKit.setDarkMode(theme === 'dark', { persist: persistedTheme !== null });
        this.bindSystemThemeIfNoUserPreference();

        // Apply initial responsive sidebar size
        this.sidebarService.handleResize(window.innerWidth);

        // Connect to notifications SignalR hub and load initial data
        if (this.isLoggedIn) {
            this.userNotificationsService.connect();
            this.userNotificationsService.loadNotifications();
            this.userNotificationsService.loadUnreadCount();
        }

        // Start inactivity checker
        this.loginService.resetInactivityTimeoutDate();
        this.inactivityCheckInterval = setInterval(() => {
            if (this.loginService.checkInactivityTimeout()) {
                this.loginService.closeSession();
            }
        }, 60000);
    }

    @HostListener('window:resize')
    onResize(): void {
        this.isMobile = window.innerWidth < 768;

        // Debounce sidebar resize (200ms) to avoid thrashing
        if (this.resizeTimer) {
            clearTimeout(this.resizeTimer);
        }
        this.resizeTimer = setTimeout(() => {
            this.sidebarService.handleResize(window.innerWidth);
        }, 200);
    }

    ngOnDestroy(): void {
        if (this.inactivityCheckInterval) {
            clearInterval(this.inactivityCheckInterval);
        }

        if (this.resizeTimer) {
            clearTimeout(this.resizeTimer);
        }

        if (this.darkMediaQuery && this.darkModeListener) {
            this.darkMediaQuery.removeEventListener('change', this.darkModeListener);
            this.darkModeListener = null;
        }

        // Disconnect from notifications SignalR hub
        this.userNotificationsService.disconnect();
    }

    @HostListener('window:keydown')
    @HostListener('window:click')
    @HostListener('window:mousemove')
    @HostListener('window:scroll')
    onUserActivity(): void {
        this.loginService.resetInactivityTimeoutDate();
    }

    private resolveInitialTheme(persistedTheme: string | null): 'dark' | 'skin-3' {
        if (persistedTheme === 'dark' || persistedTheme === 'skin-3') {
            return persistedTheme;
        }

        return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'skin-3';
    }

    private bindSystemThemeIfNoUserPreference(): void {
        if (localStorage.getItem('theme')) {
            return;
        }

        if (!window.matchMedia) {
            return;
        }

        this.darkMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this.darkModeListener = (event: MediaQueryListEvent) => {
            // The user may have picked an explicit theme since this listener was bound; once
            // there's a saved preference, the OS must stop overriding it.
            if (localStorage.getItem('theme')) {
                return;
            }
            this.themeKit.setDarkMode(event.matches, { persist: false });
        };
        this.darkMediaQuery.addEventListener('change', this.darkModeListener);
    }
}
